// Rule sync — ETag-based full-snapshot fetch against
// POST /api/v1/rules/sync/snapshot. Replaces the legacy
// watermark/cursor delta-sync. See docs/decisions/ for the ADR.
//
// On every poll the client sends the etag it has cached; the
// server returns 200 with the full filtered rule set when the
// etag has changed, or `not_modified=true` (empty body) when
// it hasn't. The full set is small (~50KB), so we trade a few
// kilobytes of bandwidth on changes for the simplicity of
// never carrying cursor state.

import type { AxiosInstance } from 'axios';
import type { Expr } from '@elka/runtime';
import schemaJSON from './schema.gen.json';

const STORAGE_KEY = 'predicate.rules.v1';
const ETAG_KEY = 'predicate.etag.v1';
// Legacy keys — cleaned up on first load below so localStorage
// doesn't accumulate dead state across the cutover.
const LEGACY_CURSOR_KEY = 'predicate.cursor.v1';

// Schema version this web build was compiled against. The server
// filters rules with min_dialect_version <= dialectVersion AND
// min_schema_version <= schemaVersion. Filter inputs are part of
// the etag namespace server-side, so a schema bump rolls the etag
// forward and triggers a full refetch.
const CLIENT_SCHEMA_VERSION: number =
  (schemaJSON as { schemaVersion?: number }).schemaVersion ?? 1;
const CLIENT_DIALECT_VERSION = CLIENT_SCHEMA_VERSION;

// ElkaRule is the protojson-camelCase shape of elka.v1.Rule. The
// walker only consumes the predicate sub-tree; the admin editor
// reads the rest.
export interface ElkaRule {
  id?: string;
  minDialectVersion?: number;
  minSchemaVersion?: number;
  evalLocality?: string;
  filter?: { scope?: string; predicate: Expr };
  validation?: { scope?: string; predicate: Expr; messageTemplate?: string; messageBody?: Expr };
  default?: { scope?: string; field?: string; when: Expr; value: Expr };
  visibility?: { scope?: string; predicate: Expr };
  display?: { scope?: string; body: Expr; surfaceId: string };
  order?: {
    scope?: string;
    primary: Expr;
    tiebreaker?: Expr;
    primaryDescending?: boolean;
    tiebreakerDescending?: boolean;
  };
}

export type RuleSet = Map<string, ElkaRule>;

// SnapshotClient is the minimum the sync logic needs from the HTTP
// layer. The default implementation (adaptAxios) calls the chores
// API client.
export interface SnapshotClient {
  snapshotRules(args: {
    dialectVersion: number;
    schemaVersion: number;
    previousEtag: string;
  }): Promise<{
    etag: string;
    notModified: boolean;
    rules: { id: string; ruleProtojson: Uint8Array }[];
  }>;
}

// syncRules calls the snapshot endpoint with the cached etag. On
// 304-equivalent (not_modified=true) the previous set is returned
// unchanged. On a content change the local cache is REPLACED in
// full — a missing id in the response is the tombstone signal by
// construction. No merging, no cursor.
export async function syncRules(
  client: SnapshotClient,
  current: RuleSet,
): Promise<RuleSet> {
  cleanLegacyState();
  const previousEtag = readEtag();
  const resp = await client.snapshotRules({
    dialectVersion: CLIENT_DIALECT_VERSION,
    schemaVersion: CLIENT_SCHEMA_VERSION,
    previousEtag,
  });
  if (resp.notModified) {
    // Server confirmed our cache is current; nothing to apply.
    // We still write the etag back in case it was empty (first
    // boot after install with pre-populated rule cache).
    if (resp.etag) writeEtag(resp.etag);
    return current;
  }
  const next = new Map<string, ElkaRule>();
  for (const r of resp.rules) {
    const text = new TextDecoder().decode(r.ruleProtojson);
    try {
      const rule = JSON.parse(text) as ElkaRule;
      next.set(r.id, rule);
    } catch {
      // Skip rules the local elka-ts can't decode (e.g. a future
      // rule shape). They simply don't appear in the cache; the
      // walker degrades gracefully via display-fallback.
    }
  }
  writeEtag(resp.etag);
  persistRules(next);
  return next;
}

// loadRules reads from localStorage on app boot. Returns an empty
// set if storage is empty or the JSON shape doesn't match.
export function loadRules(): RuleSet {
  if (typeof localStorage === 'undefined') return new Map();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, ElkaRule>;
    return new Map(Object.entries(parsed));
  } catch {
    return new Map();
  }
}

function persistRules(rules: RuleSet): void {
  if (typeof localStorage === 'undefined') return;
  const obj: Record<string, ElkaRule> = {};
  for (const [k, v] of rules) obj[k] = v;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
}

function readEtag(): string {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(ETAG_KEY) ?? '';
}

function writeEtag(etag: string): void {
  if (typeof localStorage === 'undefined') return;
  if (etag) localStorage.setItem(ETAG_KEY, etag);
}

function cleanLegacyState(): void {
  // One-shot cleanup of the dead cursor key. Cheap; safe to call
  // on every sync.
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(LEGACY_CURSOR_KEY) !== null) {
    localStorage.removeItem(LEGACY_CURSOR_KEY);
  }
}

// adaptAxios returns the SnapshotClient interface this module
// expects. We don't go through the generated proto client because
// (a) it'd drag the typed wire shape across the import graph and
// (b) the field name normalization (snake_case vs camelCase, both
// emitted by various server toolchains) is easier to handle here.
export function adaptAxios(axios: AxiosInstance): SnapshotClient {
  return {
    async snapshotRules(args) {
      const body = {
        dialectVersion: args.dialectVersion,
        schemaVersion: args.schemaVersion,
        previousEtag: args.previousEtag,
      };
      // Opt into transport retry: this is a POST only because it carries
      // a request body (the client's dialect/schema versions + etag). It
      // reads, mutates nothing, and is safe to replay — unlike the real
      // writes, which the retry policy refuses to replay by default
      // because a timeout is no proof the server didn't apply them.
      const res = await axios.post('/api/v1/rules/sync/snapshot', body, {
        retryOnTransportError: true,
      });
      const data = res.data?.data ?? res.data;
      // Accept both spellings: encoding/json uses proto's
      // snake_case json tags by default (`not_modified`,
      // `rule_protojson`); protojson tooling emits camelCase
      // (`notModified`, `ruleProtojson`). Either is correct
      // wire-format proto3 — we tolerate both so a server-side
      // codec swap can't silently regress us.
      const etag = String(data.etag ?? '');
      const notModified = Boolean(data.not_modified ?? data.notModified);
      const rulesField = (data.rules ?? []) as Array<{
        id?: string;
        rule_protojson?: string;
        ruleProtojson?: string;
      }>;
      const rules = rulesField.map((row) => {
        const b64 = row.rule_protojson ?? row.ruleProtojson ?? '';
        return {
          id: String(row.id ?? ''),
          ruleProtojson: b64
            ? Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
            : new Uint8Array(),
        };
      });
      return { etag, notModified, rules };
    },
  };
}
